import { Box, Stack, Typography } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

type SelectDateAndTimeFormProps = {
  setStartTimestamp: Dispatch<SetStateAction<number | null>>;
  setEndTimestamp: Dispatch<SetStateAction<number | null>>;
  setFormattedDate: Dispatch<SetStateAction<string>>;
  setIsPastDateTime?: Dispatch<SetStateAction<boolean>>;
};

const SelectDateAndTimeForm: FC<SelectDateAndTimeFormProps> = ({
  setStartTimestamp,
  setEndTimestamp,
  setFormattedDate,
  setIsPastDateTime,
}) => {
  const [date, setDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  const getTimestamp = (time: Dayjs | null) =>
    time
      ?.set('year', date?.year()!)
      .set('month', date?.month()!)
      .set('date', date?.date()!)
      .valueOf()! / 1000 ?? null;

  const checkIfPastDateTime = (
    selectedDate: Dayjs | null,
    selectedStartTime: Dayjs | null,
  ) => {
    if (!selectedDate || !selectedStartTime) {
      setIsPastDateTime?.(false);
      return;
    }

    // Combine the selected date with the selected start time
    const combinedDateTime = selectedDate
      .set('hour', selectedStartTime.hour())
      .set('minute', selectedStartTime.minute())
      .set('second', 0);

    // Check if combined date-time is in the past
    const isPast = combinedDateTime.isBefore(dayjs());
    setIsPastDateTime?.(isPast);
  };

  const handleDateUpdate = (newDate: Dayjs | null) => {
    setDate(newDate);
    setFormattedDate(newDate?.format('YYYY-MM-DD') ?? '');
    checkIfPastDateTime(newDate, startTime);
  };

  const handleUpdateStartTime = (newStartTime: Dayjs | null) => {
    const newEndTime = dayjs(newStartTime).add(30, 'minutes') ?? null;

    setStartTime(newStartTime);
    setEndTime(newEndTime);

    setStartTimestamp(getTimestamp(newStartTime));
    setEndTimestamp(getTimestamp(newEndTime));
    checkIfPastDateTime(date, newStartTime);
  };

  const handleUpdateEndTime = (newEndTime: Dayjs | null) => {
    setEndTime(newEndTime);
    setEndTimestamp(getTimestamp(newEndTime));
  };

  const isCombinedPast = (
    selectedDate: Dayjs | null,
    selectedStartTime: Dayjs | null,
  ) => {
    if (!selectedDate || !selectedStartTime) return false;
    const combined = selectedDate
      .set('hour', selectedStartTime.hour())
      .set('minute', selectedStartTime.minute())
      .set('second', 0);
    return combined.isBefore(dayjs());
  };

  const dateError = (() => {
    if (!date) return '';
    // check at day granularity
    if (date.isBefore(dayjs(), 'day')) return 'Selected date is in the past.';
    return '';
  })();

  const startTimeError = (() => {
    if (isCombinedPast(date, startTime))
      return 'Selected date with the start time is in the past.';
    return '';
  })();

  return (
    <Stack spacing={2}>
      <Stack spacing={2}>
        <Box>
          <Typography>Date:</Typography>
          <DatePicker
            disablePast
            value={date}
            onChange={handleDateUpdate}
            format="MM/DD/YYYY"
            sx={{
              width: '100%',
            }}
          />
          {dateError && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {dateError}
            </Typography>
          )}
        </Box>
        <Stack sx={{ width: '100%' }} direction="row" spacing={1}>
          <Box sx={{ width: '100%' }}>
            <Typography>Start time:</Typography>
            <TimePicker
              value={startTime}
              onChange={handleUpdateStartTime}
              // maxTime={endTime!}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isCombinedPast(date, startTime)
                    ? 'red !important'
                    : undefined,
                },
              }}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Typography>End time:</Typography>
            <TimePicker
              value={endTime}
              onChange={handleUpdateEndTime}
              minTime={startTime!}
              sx={{
                width: '100%',
              }}
            />
          </Box>
        </Stack>
        {startTimeError && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {startTimeError}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default SelectDateAndTimeForm;
